import { JobsCategories } from '@components/shared.types';
import { Button } from '@license-admin/boldjobs';
import { deferNavigation } from '@utils/deferNavigation';
import { useOutsideClick } from 'helper/hooks/useOutsideClick';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BtnGroup,
  BtnWrap,
  Checkbox,
  CrossIcon,
  DropCatLink,
  DropCatList,
  DropCatListItem,
  DropdownList,
  IconWrap,
} from './styled';

const VIRTUALIZATION_THRESHOLD = 20;
const ROW_HEIGHT = 32;
const MAX_LIST_HEIGHT = 320;
const OVERSCAN_ROWS = 5;

interface Props {
  children?: React.ReactNode;
  name: string;
  slug: string;
  active?: boolean;
  type?: 'submit' | 'reset' | 'button' | undefined;
  className: string;
  bgColor?: string;
  boxShadow?: string;
  position?: string;
  onToggle?: () => void;
  categoryWithCount: CategoryWithCount;
  removeFilter: (
    _slug: string,
    _name: string,
    selectedCategories?: Array<string>
  ) => void;
  handleFilterChange: (
    _filterName: string,
    _filterCategory: string,
    _name: string
  ) => string;
  selectedCategories: Array<string>;
  category?: JobsCategories[];
  handleCategoryClick?: () => void;
  handleCategoryFilter: (
    _filter: any,
    _categoryId: string,
    _categoryType: 'Parent' | 'Child'
  ) => void;
  selectedFilters: any;
  setIsActive?: any;
  filterRef?: any;
}

type CategoryWithCount = {
  [key: string]: number;
};

const FilterBtnGroup = (props: Props) => {
  const [scrollTop, setScrollTop] = useState(0);
  const isDropdownActive = props.active;
  const setActiveDropdownIndex = props.setIsActive;
  const handleFilterChange = props.handleFilterChange;
  const selectedCategories = props.selectedCategories?.length;

  const selectedCategoryName = Array.isArray(props.selectedCategories)
    ? props.selectedCategories[0]
    : props.selectedCategories;

  const changeHandler = useCallback(
    (filterName: string, filterCategory: string, name: string) => {
      deferNavigation(handleFilterChange(filterName, filterCategory, name));
    },
    [handleFilterChange]
  );
  const dropdownRef = useRef<HTMLDivElement>(null!);

  const sortedFlatCategoryEntries = useMemo(
    () =>
      Object.entries(props.categoryWithCount || {}).sort(
        ([categoryA], [categoryB]) => categoryA.localeCompare(categoryB)
      ),
    [props.categoryWithCount]
  );

  const shouldVirtualizeFlatList =
    sortedFlatCategoryEntries.length > VIRTUALIZATION_THRESHOLD;
  const virtualizedListHeight = Math.min(
    sortedFlatCategoryEntries.length * ROW_HEIGHT,
    MAX_LIST_HEIGHT
  );

  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS
  );
  const visibleRowCount =
    Math.ceil(virtualizedListHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2;
  const endIndex = Math.min(
    sortedFlatCategoryEntries.length,
    startIndex + visibleRowCount
  );

  const renderFlatCategoryRow = useCallback(
    (index: number, style: React.CSSProperties) => {
      const [category] = sortedFlatCategoryEntries[index];

      return (
        <div style={style}>
          <DropCatListItem as="div" key={category}>
            <DropCatLink
              onClick={() => changeHandler(props.slug, category, props.name)}
            >
              <>
                <Checkbox
                  type="checkbox"
                  checked={
                    props.selectedCategories &&
                    props.selectedCategories?.includes(category)
                  }
                  id={`cat-checkbox-${category.replace(/\s+/g, '-')}`}
                  aria-label={category}
                />
                {category}
              </>
            </DropCatLink>
          </DropCatListItem>
        </div>
      );
    },
    [
      changeHandler,
      props.name,
      props.selectedCategories,
      props.slug,
      sortedFlatCategoryEntries,
    ]
  );

  const isOutsideClick = useOutsideClick(dropdownRef, [props.filterRef]);

  useEffect(() => {
    if (isOutsideClick) {
      setActiveDropdownIndex?.(null);
    }
  }, [isOutsideClick, setActiveDropdownIndex]);

  useEffect(() => {
    if (!isDropdownActive) {
      setScrollTop(0);
    }
  }, [isDropdownActive]);

  const filterDropdown = (filter: any) => {
    const isParentActive =
      props.selectedCategories &&
      props.selectedCategories?.includes(JSON.stringify(filter.category.id));

    return (
      <DropCatList>
        {filter.subCategory.length > 0 &&
          filter.subCategory.map((filterSub: any, index: any) => (
            <DropCatListItem key={`subcat-${index}`} className="sub-cat">
              <DropCatLink
                onClick={() =>
                  props.handleCategoryFilter(
                    filter,
                    JSON.stringify(filterSub.id),
                    'Child'
                  )
                }
                id={`catlink-${filterSub.name.replace(/\s+/g, '-')}-${index}`}
              >
                <Checkbox
                  type="checkbox"
                  checked={
                    isParentActive
                      ? isParentActive
                      : props.selectedCategories &&
                        props.selectedCategories?.includes(
                          JSON.stringify(filterSub.id)
                        )
                  }
                  id={`checkbox-${filterSub.name.replace(
                    /\s+/g,
                    '-'
                  )}-${index}`}
                />
                {filterSub.name}
              </DropCatLink>
            </DropCatListItem>
          ))}
      </DropCatList>
    );
  };

  function findNameById(id: any, data: any): string {
    let categoryId = Array.isArray(id) ? id[0] : [id][0];
    for (const item of data) {
      if (item.category.id == categoryId) {
        return item.category.name + `${id.length > 1 ? '...' : ''}`;
      }
      for (const subCategory of item.subCategory) {
        if (subCategory.id == categoryId) {
          return (
            subCategory.name +
            `${
              Array.isArray(props.selectedCategories) &&
              props.selectedCategories.length > 1
                ? '...'
                : ''
            }`
          );
        }
      }
    }
    return 'Category';
  }

  return (
    <BtnGroup role="group" isActive={props.active}>
      {props.name && (
        <BtnWrap
          className={`${props.className} ${selectedCategories ? 'active' : ''}`}
          ref={props.filterRef}
        >
          {props.slug === 'categories' ? (
            <Button
              buttonType="light"
              text={
                props.selectedCategories && props.category
                  ? findNameById(props.selectedCategories, props.category)
                  : props.name
              }
              ButtonClass={
                selectedCategories
                  ? 'filter-group show-content'
                  : 'filter-group no-category show-content'
              }
              clickHandler={props.onToggle}
              id={`${props.name.replace(/\s+/g, '-')}`}
            />
          ) : (
            <Button
              buttonType="light"
              text={
                props.selectedCategories &&
                Array.isArray(props.selectedCategories) &&
                props.selectedCategories.length > 1 &&
                selectedCategoryName
                  ? `${selectedCategoryName}...`
                  : selectedCategoryName || props.name
              }
              ButtonClass={
                selectedCategories
                  ? 'filter-group show-content'
                  : 'filter-group no-category show-content'
              }
              ariaLabel={props.name}
              clickHandler={props.onToggle}
              id={`filter-group-btn-${props.name.replace(/\s+/g, '-')}`}
            />
          )}

          {selectedCategories > 0 && (
            <IconWrap
              id={`remove-selected-category-${props.name.replace(/\s+/g, '-')}`}
              onClick={() =>
                props.removeFilter(
                  props.slug,
                  props.name,
                  props.selectedCategories
                )
              }
              aria-label="Remove button"
            >
              <CrossIcon>
                <strong>&#10006;</strong>
              </CrossIcon>
            </IconWrap>
          )}
        </BtnWrap>
      )}
      {props.active && (
        <DropdownList className="dropdown" ref={dropdownRef}>
          <DropCatList>
            {sortedFlatCategoryEntries.length > 0 ? (
              shouldVirtualizeFlatList ? (
                <div
                  style={{ height: virtualizedListHeight, overflowY: 'auto' }}
                  onScroll={(event) => {
                    setScrollTop((event.target as HTMLDivElement).scrollTop);
                  }}
                >
                  <div
                    style={{
                      height: sortedFlatCategoryEntries.length * ROW_HEIGHT,
                      position: 'relative',
                    }}
                  >
                    {Array.from(
                      { length: endIndex - startIndex },
                      (_, offset) => {
                        const index = startIndex + offset;
                        const rowStyle: React.CSSProperties = {
                          position: 'absolute',
                          top: index * ROW_HEIGHT,
                          left: 0,
                          right: 0,
                          height: ROW_HEIGHT,
                        };

                        return (
                          <div key={`virtual-flat-row-${index}`}>
                            {renderFlatCategoryRow(index, rowStyle)}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              ) : (
                sortedFlatCategoryEntries.map(([category]) => {
                  return (
                    <DropCatListItem key={category}>
                      <DropCatLink
                        onClick={() =>
                          changeHandler(props.slug, category, props.name)
                        }
                      >
                        <>
                          <Checkbox
                            type="checkbox"
                            checked={
                              props.selectedCategories &&
                              props.selectedCategories?.includes(category)
                            }
                            id={`cat-checkbox-${category.replace(/\s+/g, '-')}`}
                            aria-label={category}
                          />
                          {category}
                        </>
                      </DropCatLink>
                    </DropCatListItem>
                  );
                })
              )
            ) : (
              <>
                {props.category
                  ?.slice() // Create a shallow copy to avoid mutating the original array
                  .sort((a, b) =>
                    a.category.name.localeCompare(b.category.name)
                  ) // Sort alphabetically by category name
                  .map((filter, index) => {
                    return (
                      <DropCatListItem key={`cat-${index}`}>
                        <DropCatLink
                          onClick={() =>
                            props.handleCategoryFilter(
                              filter,
                              JSON.stringify(filter.category.id),
                              'Parent'
                            )
                          }
                        >
                          <Checkbox
                            type="checkbox"
                            checked={
                              props.selectedCategories &&
                              props.selectedCategories?.includes(
                                JSON.stringify(filter.category.id)
                              )
                            }
                            id={`selectcat-checkbox-${filter.category.name.replace(
                              /\s+/g,
                              '-'
                            )}`}
                          />
                          {filter.category.name}
                        </DropCatLink>
                        {filterDropdown(filter)}
                      </DropCatListItem>
                    );
                  })}
              </>
            )}
          </DropCatList>
        </DropdownList>
      )}
    </BtnGroup>
  );
};

export default FilterBtnGroup;
